// BILLING-PLANS (§6.2): Operator › Billing › Companies — one row per granted company (plan,
// status, collection, people, DEs, next invoice, discount, what is open or overdue) with hover
// icons: Open billing, Plan, Collection, Discount, Retry payment (past due), New invoice, New
// project. A row expands to its DEs with the tier as an inline select (the proration path), AGs
// against the cap, its discounts (End, confirmed) and projects (status, milestone Invoice).
import { newIdempotencyKey } from "@companyos/sdk";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Checkbox,
  EmptyState,
  InfoTip,
  NativeSelect,
  NativeSelectOption,
  Segmented,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
  WorkerBadge,
  workerName,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  BadgePercentIcon,
  Building2Icon,
  ChevronDownIcon,
  ChevronRightIcon,
  ExternalLinkIcon,
  FilePlusIcon,
  HammerIcon,
  LandmarkIcon,
  LayersIcon,
  RefreshCwIcon,
  XCircleIcon,
  XIcon,
} from "lucide-react";
import { Fragment, useState } from "react";
import type { Api } from "../../../api/client";
import { ONE_TIME_CHARGES } from "../../billing/flags";
import { bpToPercentText, centsToDollarsText, parsePercentToBp } from "../../billing/money";
import { SectionHead, TIER_LABEL } from "../../billing/plan/parts";
import { MILESTONE_STATUS, PROJECT_STATUS } from "../../billing/plan/projects-card";
import { ScheduledChanges, ScheduledTier } from "../../billing/plan/scheduled";
import {
  type DiscountView,
  type MaintenanceTier,
  type OperatorBillingCompanyRow,
  type OperatorCompanyBilling,
  opBillingKeys,
  opCompaniesQuery,
  opCompanyQuery,
  type PaymentMethod,
  type ProjectView,
  priceListsQuery,
  type SubscriptionView,
} from "../../billing/plan-queries";
import { formatDate, formatMoney } from "../../format";
import { RowLinkAction } from "../../ops-ux";
import { LoadError, Loading } from "../../screen-parts";
import { DENSE_TABLE, RowAction, RowActions } from "../../settings/row-actions";
import {
  FormDialog,
  MoneyField,
  optionalCents,
  optionalInt,
  PercentField,
  SelectField,
  TextField,
} from "./form-parts";
import { NewDocumentDialog } from "./invoices";
import { PaymentMethodsField } from "./plans";
import { ProjectDialog } from "./project-dialog";

const money = (c: number) => formatMoney(c, "USD");
const TIERS: MaintenanceTier[] = ["standard", "advanced", "managed"];
const OPERATION_LABEL: Record<string, string> = {
  "subscription.put": "Plan change",
  "subscription.sync": "Seats and DEs",
  "subscription.tier": "Tier change",
  "subscription.cancel": "Cancellation",
  "subscription.renewal": "New prices at renewal",
  "invoice.create": "Invoice",
  "invoice.action": "Invoice action",
  "quote.create": "Quote",
  "quote.action": "Quote action",
  setup_fee: "Setup fee",
  "account.collection": "Collection settings",
  "customer.ensure": "Stripe customer",
};
const STATUS: Record<
  NonNullable<OperatorBillingCompanyRow["status"]>,
  { label: string; tone: "neutral" | "info" | "success" | "warning" | "danger" }
> = {
  trialing: { label: "Trial", tone: "info" },
  active: { label: "Active", tone: "success" },
  past_due: { label: "Past due", tone: "warning" },
  suspended: { label: "Suspended", tone: "danger" },
  cancelled: { label: "Cancelled", tone: "neutral" },
};

type Modal = {
  kind: "plan" | "collection" | "discount" | "project" | "invoice";
  row: OperatorBillingCompanyRow;
} | null;

function useRefresh(companyId?: string) {
  const qc = useQueryClient();
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: opBillingKeys.all() }),
      companyId
        ? qc.invalidateQueries({ queryKey: ["company", companyId, "billing"] })
        : Promise.resolve(),
    ]);
}

export function CompaniesTab({ api, initialOpen }: { api: Api; initialOpen?: string }) {
  const q = useQuery(opCompaniesQuery(api));
  const [open, setOpen] = useState<string | null>(initialOpen ?? null);
  const [modal, setModal] = useState<Modal>(null);
  const qc = useQueryClient();
  const retry = useMutation({
    mutationFn: (r: OperatorBillingCompanyRow) =>
      api.client.call("operator.billing.subscription.retry", {
        params: { company_id: r.company_id },
      }),
    onSuccess: (b) =>
      toast.add({
        title: b.subscription?.status === "active" ? "Payment went through" : "Retried",
        type: b.subscription?.status === "active" ? "success" : "warning",
      }),
    onSettled: () => qc.invalidateQueries({ queryKey: opBillingKeys.all() }),
  });
  if (q.isPending) return <Loading label="Loading companies" rows={4} />;
  if (q.isError) return <LoadError what="Companies" error={q.error} />;
  const rows = q.data.data;
  if (!rows.length)
    return <EmptyState variant="card" icon={Building2Icon} title="No companies granted" />;
  const close = () => setModal(null);
  return (
    <>
      <Table className={DENSE_TABLE} data-slot="op-billing-companies">
        <TableCaption className="sr-only">Each granted company's billing.</TableCaption>
        <TableHeader>
          <TableRow>
            <TableHead scope="col" className="w-8">
              <span className="sr-only">Expand</span>
            </TableHead>
            <TableHead scope="col">Company</TableHead>
            <TableHead scope="col">Plan</TableHead>
            <TableHead scope="col">Status</TableHead>
            <TableHead scope="col">Collection</TableHead>
            <TableHead scope="col" className="text-right">
              People
            </TableHead>
            <TableHead scope="col" className="text-right">
              DEs
            </TableHead>
            <TableHead scope="col" className="text-right">
              Next invoice
            </TableHead>
            <TableHead scope="col">Discount</TableHead>
            <TableHead scope="col" className="text-right">
              Open / overdue
            </TableHead>
            <TableHead scope="col">
              <span className="sr-only">Actions</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => {
            const expanded = open === r.company_id;
            return (
              <Fragment key={r.company_id}>
                <TableRow className="group/row" data-company={r.slug}>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-expanded={expanded}
                      aria-label={`${expanded ? "Collapse" : "Expand"} ${r.name}`}
                      onClick={() => setOpen(expanded ? null : r.company_id)}
                    >
                      {expanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
                    </Button>
                  </TableCell>
                  <TableHead scope="row" className="font-medium">
                    {r.name}
                  </TableHead>
                  <TableCell>{r.plan_name ?? "—"}</TableCell>
                  <TableCell>
                    {r.status ? (
                      <span className="inline-flex items-center gap-1">
                        <Badge variant={STATUS[r.status].tone}>{STATUS[r.status].label}</Badge>
                        {r.grace_until ? (
                          <InfoTip label={`About ${r.name}'s status`}>
                            Suspended on {formatDate(r.grace_until)} unless paid.
                          </InfoTip>
                        ) : null}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">No plan</span>
                    )}
                  </TableCell>
                  <TableCell>{r.collection_method === "invoice" ? "Invoice" : "Card"}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.people_qty}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.des}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {r.next_invoice_minor === null ? "—" : money(r.next_invoice_minor)}
                  </TableCell>
                  <TableCell>{r.discount_label ?? "—"}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {r.open_minor ? money(r.open_minor) : "—"}
                    {r.overdue ? (
                      <span className="ml-1 text-danger-text">· {r.overdue} overdue</span>
                    ) : null}
                  </TableCell>
                  <TableCell className="w-px">
                    <RowActions>
                      <RowLinkAction
                        icon={ExternalLinkIcon}
                        label={`Open ${r.name}'s billing`}
                        tooltip="Open billing"
                        render={<Link to="/c/$slug/setup/billing" params={{ slug: r.slug }} />}
                      />
                      <RowAction
                        icon={LayersIcon}
                        label={`Plan of ${r.name}`}
                        tip="Plan"
                        onClick={() => setModal({ kind: "plan", row: r })}
                      />
                      <RowAction
                        icon={LandmarkIcon}
                        label={`Collection of ${r.name}`}
                        tip="Collection"
                        onClick={() => setModal({ kind: "collection", row: r })}
                      />
                      <RowAction
                        icon={BadgePercentIcon}
                        label={`Discount for ${r.name}`}
                        tip="Discount"
                        onClick={() => setModal({ kind: "discount", row: r })}
                      />
                      {r.status === "past_due" || r.status === "suspended" ? (
                        <RowAction
                          icon={RefreshCwIcon}
                          label={`Retry ${r.name}'s payment`}
                          tip="Retry payment"
                          disabled={retry.isPending}
                          onClick={() => retry.mutate(r)}
                        />
                      ) : null}
                      {ONE_TIME_CHARGES ? (
                        <>
                          <RowAction
                            icon={FilePlusIcon}
                            label={`New invoice or quote for ${r.name}`}
                            tip="New invoice or quote"
                            onClick={() => setModal({ kind: "invoice", row: r })}
                          />
                          <RowAction
                            icon={HammerIcon}
                            label={`New project for ${r.name}`}
                            tip="New project"
                            onClick={() => setModal({ kind: "project", row: r })}
                          />
                        </>
                      ) : null}
                    </RowActions>
                  </TableCell>
                </TableRow>
                {expanded ? (
                  <TableRow data-company-detail={r.slug}>
                    <TableCell colSpan={11} className="bg-sunken/40 px-6 py-4">
                      <CompanyDetail api={api} companyId={r.company_id} />
                    </TableCell>
                  </TableRow>
                ) : null}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
      {modal?.kind === "plan" ? <PlanDialog api={api} row={modal.row} onClose={close} /> : null}
      {modal?.kind === "collection" ? (
        <CollectionDialog api={api} row={modal.row} onClose={close} />
      ) : null}
      {modal?.kind === "discount" ? (
        <DiscountDialog api={api} row={modal.row} onClose={close} />
      ) : null}
      {ONE_TIME_CHARGES && modal?.kind === "project" ? (
        <ProjectDialog
          api={api}
          companyId={modal.row.company_id}
          companyName={modal.row.name}
          onClose={close}
        />
      ) : null}
      {ONE_TIME_CHARGES && modal?.kind === "invoice" ? (
        <NewDocumentDialog api={api} companyId={modal.row.company_id} onClose={close} />
      ) : null}
    </>
  );
}

// Plan -------------------------------------------------------------------------------------------

/** "2026-11-30T00:00:00.000Z" → "2026-11-30". */
const dayOf = (ts: string | null | undefined) => (ts ? ts.slice(0, 10) : "");
const centsText = (c: number | null | undefined) =>
  c === null || c === undefined ? "" : centsToDollarsText(c);

export function PlanDialog({
  api,
  row,
  onClose,
}: {
  api: Api;
  row: OperatorBillingCompanyRow;
  onClose: () => void;
}) {
  const billing = useQuery(opCompanyQuery(api, row.company_id));
  const prices = useQuery(priceListsQuery(api));
  if (billing.isPending || prices.isPending) return null;
  if (billing.isError || prices.isError) return null;
  return (
    <PlanForm
      api={api}
      row={row}
      billing={billing.data}
      keys={[
        ...new Set(prices.data.platform.filter((p) => p.active && p.current).map((p) => p.key)),
      ]}
      onClose={onClose}
    />
  );
}

function PlanForm({
  api,
  row,
  billing,
  keys,
  onClose,
}: {
  api: Api;
  row: OperatorBillingCompanyRow;
  billing: OperatorCompanyBilling;
  keys: string[];
  onClose: () => void;
}) {
  const refresh = useRefresh(row.company_id);
  const o = billing.overrides;
  const sub = billing.subscription;
  const [f, setF] = useState({
    key: sub?.plan.key ?? keys[0] ?? "",
    trial: dayOf(sub?.trial_end),
    trialEdited: false,
    grace: o.grace_days === null ? "" : String(o.grace_days),
    base: centsText(o.base_price_override_minor),
    extra: centsText(o.extra_seat_price_override_minor),
    included: o.included_seats_override === null ? "" : String(o.included_seats_override),
    standard: centsText(o.maintenance_overrides.standard),
    advanced: centsText(o.maintenance_overrides.advanced),
    managed: centsText(o.maintenance_overrides.managed),
  });
  const set = (p: Partial<typeof f>) => setF((x) => ({ ...x, ...p }));
  const grace = optionalInt(f.grace);
  const included = optionalInt(f.included);
  const base = optionalCents(f.base);
  const extra = optionalCents(f.extra);
  const tiers = {
    standard: optionalCents(f.standard),
    advanced: optionalCents(f.advanced),
    managed: optionalCents(f.managed),
  };
  const valid =
    f.key !== "" &&
    grace !== undefined &&
    (grace === null || grace <= 90) &&
    included !== undefined &&
    base !== undefined &&
    extra !== undefined &&
    TIERS.every((t) => tiers[t] !== undefined);
  const save = useMutation({
    mutationFn: () => {
      const maintenance: Partial<Record<MaintenanceTier, number>> = {};
      for (const t of TIERS) {
        const v = tiers[t];
        if (typeof v === "number") maintenance[t] = v;
      }
      return api.client.call("operator.billing.subscription.put", {
        params: { company_id: row.company_id },
        body: {
          plan_key: f.key,
          // only when edited: an ended trial, or its exact time, is never resent
          ...(f.trialEdited ? { trial_end: f.trial ? `${f.trial}T00:00:00.000Z` : null } : {}),
          grace_days: grace ?? null,
          base_price_override_minor: base ?? null,
          extra_seat_price_override_minor: extra ?? null,
          included_seats_override: included ?? null,
          maintenance_overrides: maintenance,
        },
      });
    },
    onSuccess: () => {
      toast.add({ title: `${row.name}'s plan saved`, type: "success" });
      onClose();
    },
    onSettled: refresh,
  });
  return (
    <FormDialog
      open
      wide
      onOpenChange={(open) => (open ? null : onClose())}
      title={`Plan for ${row.name}`}
      pending={save.isPending}
      canSubmit={valid}
      submitLabel={sub ? "Save plan" : "Start plan"}
      onSubmit={() => save.mutate()}
    >
      <div className="grid gap-4 sm:grid-cols-3">
        <SelectField
          label="Plan"
          value={f.key}
          options={keys.map((k) => ({ value: k, label: k }))}
          onChange={(key) => set({ key })}
        />
        <TextField
          label="Trial ends"
          type="date"
          value={f.trial}
          onChange={(trial) => set({ trial, trialEdited: true })}
          info="Blank: no trial; the first period is billed now."
        />
        <TextField
          label="Grace days"
          inputMode="numeric"
          placeholder="Plan's"
          value={f.grace}
          onChange={(g) => set({ grace: g })}
        />
      </div>
      <fieldset className="m-0 grid gap-4 border-0 p-0 sm:grid-cols-3">
        <legend className="mb-2 flex items-center gap-1 text-sm font-medium">
          Price overrides
          <InfoTip label="About price overrides">Blank: the list price.</InfoTip>
        </legend>
        <MoneyField
          label="Plan ($ / month)"
          value={f.base}
          onChange={(b) => set({ base: b })}
          optional
        />
        <MoneyField
          label="Per extra person ($)"
          value={f.extra}
          onChange={(e) => set({ extra: e })}
          optional
        />
        <TextField
          label="People included"
          inputMode="numeric"
          placeholder="Plan's"
          value={f.included}
          onChange={(i) => set({ included: i })}
        />
        {TIERS.map((t) => (
          <MoneyField
            key={t}
            label={`${TIER_LABEL[t]} ($ / DE)`}
            value={f[t]}
            onChange={(v) => set({ [t]: v })}
            optional
          />
        ))}
      </fieldset>
    </FormDialog>
  );
}

// Collection -------------------------------------------------------------------------------------

function CollectionDialog({
  api,
  row,
  onClose,
}: {
  api: Api;
  row: OperatorBillingCompanyRow;
  onClose: () => void;
}) {
  const billing = useQuery(opCompanyQuery(api, row.company_id));
  if (billing.isPending || billing.isError) return null;
  return <CollectionForm api={api} row={row} billing={billing.data} onClose={onClose} />;
}

function CollectionForm({
  api,
  row,
  billing,
  onClose,
}: {
  api: Api;
  row: OperatorBillingCompanyRow;
  billing: OperatorCompanyBilling;
  onClose: () => void;
}) {
  const refresh = useRefresh(row.company_id);
  const a = billing.account;
  const presetNet = ["15", "30", "45"].includes(String(a.net_terms_days));
  // the form starts from what the company has: its own terms, or the platform's (inherited)
  const inheritNet = billing.overrides.net_terms_days === null;
  const [f, setF] = useState({
    method: a.collection_method,
    net: inheritNet ? "default" : presetNet ? String(a.net_terms_days) : "custom",
    customNet: String(a.net_terms_days),
    defaultMethods: billing.overrides.payment_methods === null,
    methods: a.payment_methods as PaymentMethod[],
    email: a.billing_email ?? "",
    fee: centsText(billing.overrides.setup_fee_minor),
    topups: a.invoice_topups_allowed,
  });
  const set = (p: Partial<typeof f>) => setF((x) => ({ ...x, ...p }));
  // "default": the platform's net terms (inherited, following later changes of the default)
  const netText =
    f.net === "default" ? String(a.net_terms_days) : f.net === "custom" ? f.customNet : f.net;
  const fee = optionalCents(f.fee);
  const emailOk = f.email.trim() === "" || /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email.trim());
  const valid =
    /^\d+$/.test(netText) &&
    Number(netText) <= 120 &&
    fee !== undefined &&
    emailOk &&
    (f.defaultMethods || f.methods.length > 0);
  const save = useMutation({
    mutationFn: () =>
      api.client.call("operator.billing.collection.put", {
        params: { company_id: row.company_id },
        body: {
          collection_method: f.method,
          // untouched inherited terms stay inherited
          net_terms_days: f.net === "default" ? null : Number(netText),
          payment_methods: f.defaultMethods ? null : f.methods,
          billing_email: f.email.trim() || null,
          setup_fee_minor: fee ?? null,
          invoice_topups_allowed: f.topups,
        },
      }),
    onSuccess: () => {
      toast.add({ title: `${row.name}'s collection saved`, type: "success" });
      onClose();
    },
    onSettled: refresh,
  });
  return (
    <FormDialog
      open
      onOpenChange={(open) => (open ? null : onClose())}
      title={`Collection for ${row.name}`}
      pending={save.isPending}
      canSubmit={valid}
      submitLabel="Save"
      onSubmit={() => save.mutate()}
    >
      <div className="flex items-center gap-2">
        <Segmented
          aria-label="Collection method"
          value={f.method}
          onValueChange={(method) => set({ method })}
          options={[
            { value: "card", label: "Card" },
            { value: "invoice", label: "Invoice" },
          ]}
        />
        <InfoTip label="About collection">
          Card: charged automatically. Invoice: sent with net terms; paid by card, ACH or bank
          transfer. Applies from the next invoice.
        </InfoTip>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          label="Net terms"
          value={f.net}
          options={[
            { value: "default", label: "Platform default" },
            { value: "15", label: "Net 15" },
            { value: "30", label: "Net 30" },
            { value: "45", label: "Net 45" },
            { value: "custom", label: "Custom" },
          ]}
          onChange={(net) => set({ net })}
        />
        {f.net === "custom" ? (
          <TextField
            label="Days"
            inputMode="numeric"
            value={f.customNet}
            onChange={(customNet) => set({ customNet })}
            required
          />
        ) : null}
        <TextField
          label="Billing email"
          type="email"
          value={f.email}
          onChange={(email) => set({ email })}
          placeholder="Owners' emails"
        />
        {ONE_TIME_CHARGES ? (
          <MoneyField
            label="Setup fee override ($)"
            value={f.fee}
            onChange={(fee) => set({ fee })}
            optional
          />
        ) : null}
      </div>
      <span className="flex items-center gap-2 text-[13px]">
        <Checkbox
          checked={f.defaultMethods}
          aria-label="Default payment methods"
          onCheckedChange={(c) => set({ defaultMethods: c === true })}
        />
        Default payment methods
      </span>
      {f.defaultMethods ? null : (
        <PaymentMethodsField value={f.methods} onChange={(methods) => set({ methods })} />
      )}
      {ONE_TIME_CHARGES ? (
        <span className="flex items-center gap-2 text-[13px]">
          <Checkbox
            checked={f.topups}
            aria-label="Credit by invoice allowed"
            onCheckedChange={(c) => set({ topups: c === true })}
          />
          Credit by invoice allowed
          <InfoTip label="About credit by invoice">
            The client may buy prepaid credit by invoice; it lands when paid.
          </InfoTip>
        </span>
      ) : null}
    </FormDialog>
  );
}

// Discount ---------------------------------------------------------------------------------------

function DiscountDialog({
  api,
  row,
  onClose,
}: {
  api: Api;
  row: OperatorBillingCompanyRow;
  onClose: () => void;
}) {
  const refresh = useRefresh(row.company_id);
  const [f, setF] = useState({
    label: "Founding client",
    percent: "30",
    months: "6",
    applies: "all_recurring" as DiscountView["applies_to"],
    starts: "",
  });
  const set = (p: Partial<typeof f>) => setF((x) => ({ ...x, ...p }));
  const bp = parsePercentToBp(f.percent);
  const months = optionalInt(f.months);
  const valid =
    f.label.trim() !== "" &&
    bp !== null &&
    bp >= 1 &&
    typeof months === "number" &&
    months >= 1 &&
    months <= 36;
  const save = useMutation({
    mutationFn: () =>
      api.client.call("operator.billing.discounts.create", {
        params: { company_id: row.company_id },
        idempotencyKey: newIdempotencyKey(),
        body: {
          label: f.label.trim(),
          percent_bp: bp ?? 0,
          months: typeof months === "number" ? months : 1,
          applies_to: f.applies,
          ...(f.starts ? { starts_on: f.starts } : {}),
        },
      }),
    onSuccess: (d) => {
      toast.add({
        title: `${d.label}: ${bpToPercentText(d.percent_bp)}% off until ${formatDate(d.ends_on)}`,
        type: "success",
      });
      onClose();
    },
    onSettled: refresh,
  });
  return (
    <FormDialog
      open
      onOpenChange={(open) => (open ? null : onClose())}
      title={`Discount for ${row.name}`}
      pending={save.isPending}
      canSubmit={valid}
      submitLabel="Apply discount"
      onSubmit={() => save.mutate()}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          label="Label"
          value={f.label}
          onChange={(label) => set({ label })}
          maxLength={60}
          required
        />
        <PercentField label="Off" value={f.percent} onChange={(percent) => set({ percent })} />
        <TextField
          label="Months"
          inputMode="numeric"
          value={f.months}
          onChange={(m) => set({ months: m })}
          required
        />
        <SelectField
          label="Applies to"
          value={f.applies}
          options={[
            { value: "all_recurring", label: "Plan and maintenance" },
            { value: "plans", label: "Plan only" },
            { value: "maintenance", label: "Maintenance only" },
          ]}
          onChange={(applies) => set({ applies })}
          info="Never credit or one-off invoices."
        />
        <TextField
          label="Starts on"
          type="date"
          value={f.starts}
          onChange={(starts) => set({ starts })}
          info="Blank: today. A later day extends a live discount."
        />
      </div>
    </FormDialog>
  );
}

// Expanded row -----------------------------------------------------------------------------------

function CompanyDetail({ api, companyId }: { api: Api; companyId: string }) {
  const q = useQuery(opCompanyQuery(api, companyId));
  const refresh = useRefresh(companyId);
  const [ending, setEnding] = useState<DiscountView | null>(null);
  const [invoicing, setInvoicing] = useState<{
    project: ProjectView["project"];
    milestone: ProjectView["milestones"][number];
  } | null>(null);
  const tier = useMutation({
    mutationFn: (v: { membershipId: string; tier: MaintenanceTier }) =>
      api.client.call("operator.billing.maintenance.put", {
        params: { company_id: companyId, membership_id: v.membershipId },
        body: { tier: v.tier },
      }),
    onSuccess: (l) =>
      toast.add({
        title: l.scheduled_tier
          ? `${workerName(l.name)} moves to ${TIER_LABEL[l.scheduled_tier]} on ${
              l.scheduled_at ? formatDate(l.scheduled_at) : "the renewal"
            }`
          : `${workerName(l.name)} is on ${l.tier_name ?? "a new tier"}`,
        type: "success",
      }),
    onSettled: refresh,
  });
  // a plan change waiting for the renewal, cancelled by sending the paid terms back
  const revert = useMutation({
    mutationFn: (body: NonNullable<NonNullable<SubscriptionView["scheduled"]>["revert"]>) =>
      api.client.call("operator.billing.subscription.put", {
        params: { company_id: companyId },
        body,
      }),
    onSuccess: () => toast.add({ title: "Plan change cancelled", type: "success" }),
    onSettled: refresh,
  });
  const end = useMutation({
    mutationFn: (d: DiscountView) =>
      api.client.call("operator.billing.discounts.end", {
        params: { company_id: companyId, discount_id: d.id },
      }),
    onSuccess: () => setEnding(null),
    onSettled: refresh,
  });
  const status = useMutation({
    mutationFn: (v: { projectId: string; status: ProjectView["project"]["status"] }) =>
      api.client.call("operator.billing.projects.update", {
        params: { company_id: companyId, project_id: v.projectId },
        body: { status: v.status },
      }),
    onSettled: refresh,
  });
  const invoice = useMutation({
    mutationFn: (v: { projectId: string; milestoneId: string }) =>
      api.client.call("operator.billing.milestones.invoice", {
        params: { company_id: companyId, project_id: v.projectId, milestone_id: v.milestoneId },
        idempotencyKey: newIdempotencyKey(),
        body: { send: true },
      }),
    onSuccess: (inv) => {
      setInvoicing(null);
      toast.add({ title: `Invoice ${inv.number ?? ""} sent`, type: "success" });
    },
    onSettled: refresh,
  });
  const operation = useMutation({
    mutationFn: (v: { id: string; action: "retry" | "dismiss" }) =>
      api.client.call(
        v.action === "retry"
          ? "operator.billing.operations.retry"
          : "operator.billing.operations.dismiss",
        { params: { company_id: companyId, operation_id: v.id } },
      ),
    onSuccess: (_b, v) =>
      toast.add({ title: v.action === "retry" ? "Retried" : "Dismissed", type: "success" }),
    onSettled: refresh,
  });
  if (q.isPending) return <Loading label="Loading the company's billing" rows={2} />;
  if (q.isError) return <LoadError what="The company's billing" error={q.error} />;
  const b = q.data;
  const onPlan = b.subscription !== null && b.subscription.status !== "cancelled";
  return (
    <div className="flex flex-col gap-5" data-slot="company-billing-detail">
      {b.subscription?.scheduled ? (
        <section aria-label="Scheduled changes">
          <ScheduledChanges
            scheduled={b.subscription.scheduled}
            currency={b.subscription.plan.currency}
            cancelling={revert.isPending}
            onCancel={() => {
              const r = b.subscription?.scheduled?.revert;
              if (r) revert.mutate(r);
            }}
          />
        </section>
      ) : null}
      {b.operations.length > 0 ? (
        <section
          aria-label="Billing changes"
          className="flex flex-col gap-1.5"
          data-slot="billing-operations"
        >
          <span className="inline-flex items-center gap-1 text-[13px] font-medium">
            Changes in progress
            <InfoTip label="About changes in progress">
              Saved changes on their way to Stripe, retried automatically every minute. A failed one
              stays until you retry it (what it made at Stripe is reused, never doubled) or dismiss
              it.
            </InfoTip>
          </span>
          <ul className="flex flex-col gap-1 text-[13px]">
            {b.operations.map((o) => (
              <li key={o.id} className="group/row flex flex-wrap items-center gap-2">
                <Badge variant={o.status === "failed" ? "destructive" : "secondary"}>
                  {o.status === "failed" ? "Failed" : "Pending"}
                </Badge>
                <span>{OPERATION_LABEL[o.kind] ?? o.kind}</span>
                {o.error ? <span className="text-muted-foreground">{o.error}</span> : null}
                {o.status === "failed" ? (
                  <RowActions>
                    <RowAction
                      icon={RefreshCwIcon}
                      label={`Retry ${OPERATION_LABEL[o.kind] ?? o.kind}`}
                      tip="Retry"
                      disabled={operation.isPending}
                      onClick={() => operation.mutate({ id: o.id, action: "retry" })}
                    />
                    <RowAction
                      icon={XIcon}
                      label={`Dismiss ${OPERATION_LABEL[o.kind] ?? o.kind}`}
                      tip="Dismiss"
                      disabled={operation.isPending}
                      onClick={() => operation.mutate({ id: o.id, action: "dismiss" })}
                    />
                  </RowActions>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      <section aria-label="DEs" className="flex flex-col gap-1.5">
        <SectionHead id={`des-${companyId}`} title="DEs" />
        {b.agents.length === 0 ? (
          <p className="text-[13px] text-muted-foreground">No digital employees.</p>
        ) : (
          <Table className={DENSE_TABLE}>
            <TableCaption className="sr-only">The company's DEs and their tiers.</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead scope="col">DE</TableHead>
                <TableHead scope="col">
                  <span className="inline-flex items-center gap-1">
                    Tier
                    {onPlan ? null : (
                      <InfoTip label="About tiers">Put the company on a plan first.</InfoTip>
                    )}
                  </span>
                </TableHead>
                <TableHead scope="col" className="text-right">
                  AGs
                </TableHead>
                <TableHead scope="col" className="text-right">
                  Monthly
                </TableHead>
                <TableHead scope="col">AGs included</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {b.agents.map((a) => (
                <TableRow key={a.membership_id} data-de={a.name}>
                  <TableHead scope="row" className="font-medium">
                    <span className="flex items-center gap-1.5">
                      <WorkerBadge kind="de" size="sm" />
                      {workerName(a.name)}
                    </span>
                  </TableHead>
                  <TableCell>
                    <NativeSelect
                      size="sm"
                      aria-label={`${workerName(a.name)}'s tier`}
                      value={a.tier ?? ""}
                      disabled={!onPlan || tier.isPending}
                      onChange={(e) =>
                        tier.mutate({
                          membershipId: a.membership_id,
                          tier: e.target.value as MaintenanceTier,
                        })
                      }
                    >
                      {a.tier === null ? <NativeSelectOption value="">—</NativeSelectOption> : null}
                      {TIERS.map((t) => (
                        <NativeSelectOption key={t} value={t}>
                          {TIER_LABEL[t]}
                        </NativeSelectOption>
                      ))}
                    </NativeSelect>
                    <ScheduledTier
                      tier={a.scheduled_tier}
                      at={a.scheduled_at}
                      cancelling={tier.isPending}
                      onCancel={
                        a.tier
                          ? () =>
                              tier.mutate({
                                membershipId: a.membership_id,
                                tier: a.tier as MaintenanceTier,
                              })
                          : undefined
                      }
                    />
                  </TableCell>
                  <TableCell
                    className={`text-right tabular-nums ${
                      a.ag_cap !== null && a.ag_count >= a.ag_cap ? "text-warning-text" : ""
                    }`}
                  >
                    {a.ag_cap === null ? a.ag_count : `${a.ag_count} / ${a.ag_cap}`}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {a.monthly_minor === null ? "—" : money(a.monthly_minor)}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {a.agents.map((x) => x.name).join(", ") || "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </section>
      {b.discounts.length ? (
        <section aria-label="Discounts" className="flex flex-col gap-1.5">
          <SectionHead id={`discounts-${companyId}`} title="Discounts" />
          <Table className={DENSE_TABLE}>
            <TableCaption className="sr-only">The company's discounts.</TableCaption>
            <TableBody>
              {b.discounts.map((d) => {
                const ended =
                  d.ended_at !== null || d.ends_on <= new Date().toISOString().slice(0, 10);
                return (
                  <TableRow key={d.id} className="group/row" data-discount={d.label}>
                    <TableHead scope="row" className="font-medium">
                      {d.label}
                    </TableHead>
                    <TableCell className="tabular-nums">
                      {bpToPercentText(d.percent_bp)}% off
                    </TableCell>
                    <TableCell>
                      {formatDate(d.starts_on)} – {formatDate(d.ends_on)}
                    </TableCell>
                    <TableCell>
                      <Badge variant={d.live ? "success" : "neutral"}>
                        {d.live ? (d.ended_at ? "Ending" : "Live") : ended ? "Ended" : "Scheduled"}
                      </Badge>
                    </TableCell>
                    <TableCell className="w-px">
                      {d.ended_at || ended ? null : (
                        <RowActions>
                          <RowAction
                            icon={XCircleIcon}
                            label={`End ${d.label}`}
                            tip="End"
                            className="hover:text-danger-text"
                            onClick={() => setEnding(d)}
                          />
                        </RowActions>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </section>
      ) : null}
      {ONE_TIME_CHARGES && b.projects.length ? (
        <section aria-label="Projects" className="flex flex-col gap-3">
          <SectionHead id={`projects-${companyId}`} title="Projects" />
          {b.projects.map(({ project: p, milestones, settled_by_invoice_id: settled }) => (
            <div key={p.id} className="flex flex-col gap-1.5" data-project={p.name}>
              <div className="flex flex-wrap items-center gap-2 text-[13px]">
                <span className="font-medium">{p.name}</span>
                <span className="text-muted-foreground">{money(p.total_minor)}</span>
                <NativeSelect
                  size="sm"
                  aria-label={`${p.name}'s status`}
                  value={p.status}
                  disabled={status.isPending}
                  onChange={(e) =>
                    status.mutate({
                      projectId: p.id,
                      status: e.target.value as ProjectView["project"]["status"],
                    })
                  }
                >
                  {Object.entries(PROJECT_STATUS).map(([v, label]) => (
                    <NativeSelectOption key={v} value={v}>
                      {label}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
              </div>
              <Table className={DENSE_TABLE}>
                <TableCaption className="sr-only">{p.name}'s milestones.</TableCaption>
                <TableBody>
                  {milestones.map((m) => (
                    <TableRow key={m.id} className="group/row" data-milestone={m.name}>
                      <TableHead scope="row" className="font-normal">
                        {m.name}
                      </TableHead>
                      <TableCell className="text-right tabular-nums">
                        {money(m.amount_minor)}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {m.due_on ? formatDate(m.due_on) : "—"}
                      </TableCell>
                      <TableCell>
                        {settled && m.status === "pending" ? (
                          <Badge variant="neutral">Billed by quote</Badge>
                        ) : (
                          <Badge variant={MILESTONE_STATUS[m.status].tone}>
                            {MILESTONE_STATUS[m.status].label}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="w-px">
                        {m.status === "pending" && !settled ? (
                          <RowActions>
                            <RowAction
                              icon={FilePlusIcon}
                              label={`Invoice ${m.name}`}
                              tip="Invoice"
                              onClick={() => setInvoicing({ project: p, milestone: m })}
                            />
                          </RowActions>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ))}
        </section>
      ) : null}
      <AlertDialog open={ending !== null} onOpenChange={(o) => (o ? null : setEnding(null))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>End “{ending?.label}”?</AlertDialogTitle>
            <AlertDialogDescription>
              It comes off at the next renewal, never mid-period.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={() => ending && end.mutate(ending)}>
              End discount
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={invoicing !== null} onOpenChange={(o) => (o ? null : setInvoicing(null))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Invoice “{invoicing?.milestone.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              {invoicing ? money(invoicing.milestone.amount_minor) : ""} is invoiced and sent now.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Not now</AlertDialogCancel>
            <AlertDialogAction
              disabled={invoice.isPending}
              onClick={() =>
                invoicing &&
                invoice.mutate({
                  projectId: invoicing.project.id,
                  milestoneId: invoicing.milestone.id,
                })
              }
            >
              Send invoice
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
